// AppBottomNav — mobile bottom navigation (visible md:hidden)
import { Link, useLocation } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { useInboxBadgeCount } from "../../hooks/useInbox";
import {
  buildBrandScopedPath,
  isBrandHubPath,
  MOBILE_BRAND_OWNED_SEGMENTS,
  parseBrandIdFromPath,
} from "../../lib/brand-navigation";
import { routeSection } from "../../lib/shell-routes";

export type MobileNavOwner = "workspace" | "campaigns" | "inbox" | "activity" | "tasks" | "brand" | null;

/**
 * Assign every deep-linkable surface to a mobile destination.
 * This keeps contextual pages reachable from their owning surface without
 * reintroducing Docs and Operations as competing primary tabs.
 */
export function mobileNavOwner(pathname: string, brandId?: string | null): MobileNavOwner {
  if (pathname === "/home" || pathname.startsWith("/outputs/")) return "workspace";

  if (brandId && (pathname === `/brand/${brandId}/campaigns` || pathname.startsWith(`/brand/${brandId}/campaigns/`))) return "campaigns";
  if (brandId && (pathname === `/brand/${brandId}/actions` || pathname.startsWith(`/brand/${brandId}/actions/`))) return "tasks";

  const isBrandLibrary =
    !!brandId &&
    (pathname === `/brand/${brandId}/docs` || pathname.startsWith(`/brand/${brandId}/assets`));
  const isMobileBrandOwned =
    !!brandId &&
    MOBILE_BRAND_OWNED_SEGMENTS.some((segment) => pathname.startsWith(`/brand/${brandId}/${segment}`));
  if (isBrandHubPath(pathname, brandId) || isBrandLibrary || isMobileBrandOwned) return "brand";

  if (pathname === "/inbox" || pathname.startsWith("/review/") || pathname.startsWith("/gates/") ||
    (!!brandId && pathname === `/brand/${brandId}/review`)) return "inbox";
  if (pathname === "/runs" || pathname.startsWith("/runs/") || pathname === "/outputs") return "activity";
  if (pathname === "/tasks" || pathname.startsWith("/tasks/")) return "tasks";
  if (pathname === "/workflows") return "workspace";

  // Keep newly added workflow detail routes oriented to the action surface until
  // they receive their own intentional mobile owner above.
  return routeSection(pathname) === "workflows" ? "inbox" : null;
}

export function AppBottomNav() {
  const location = useLocation();
  const { activeBrandId: brandId } = useActiveBrand();
  const routeBrandId = parseBrandIdFromPath(location.pathname);
  const resolvedBrandId = routeBrandId ?? brandId;
  const { count: inboxCount, status: inboxStatus } = useInboxBadgeCount(resolvedBrandId);
  const brandHref = resolvedBrandId ? `/brand/${resolvedBrandId}/settings/profile` : "/brand";
  const inboxHref = buildBrandScopedPath("/inbox", resolvedBrandId);
  const activityHref = buildBrandScopedPath("/runs", resolvedBrandId);
  const tasksHref = buildBrandScopedPath("/tasks", resolvedBrandId);
  const activeOwner = mobileNavOwner(location.pathname, resolvedBrandId);
  const inboxBadge = inboxStatus === "error" || inboxCount <= 0
    ? null
    : inboxCount > 9
      ? "9+"
      : String(inboxCount);

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-2 text-[10px] font-medium transition-colors ${
      isActive ? "bg-brand-50 text-brand-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
    }`;

  return (
    <nav
      className="safe-bottom fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/80 bg-white/95 px-3 py-2 backdrop-blur md:hidden"
      aria-label="Mobile navigation"
    >
      <div className="mx-auto flex max-w-xl items-center justify-between gap-1 rounded-[20px] border border-slate-200 bg-white px-2 py-1.5 shadow-sm">
        <Link to="/home" className={tabClass({ isActive: activeOwner === "workspace" })} aria-current={activeOwner === "workspace" ? "page" : undefined}>
          <WorkspaceIcon />
          <span>Workspace</span>
        </Link>
        {resolvedBrandId ? <Link to={`/brand/${encodeURIComponent(resolvedBrandId)}/campaigns`} className={tabClass({ isActive: activeOwner === "campaigns" })} aria-current={activeOwner === "campaigns" ? "page" : undefined}>
          <CampaignsIcon /><span>Campaigns</span>
        </Link> : null}
        <Link to={inboxHref} className={tabClass({ isActive: activeOwner === "inbox" })} aria-current={activeOwner === "inbox" ? "page" : undefined}>
          <span className="relative">
            <InboxIcon />
            {inboxBadge ? (
              <span
                aria-label={`${inboxBadge} unread item${inboxBadge === "1" ? "" : "s"}`}
                className="absolute -right-2 -top-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 py-0.5 text-[9px] font-semibold text-white"
              >
                {inboxBadge}
              </span>
            ) : null}
          </span>
          <span>Inbox</span>
        </Link>
        <Link to={activityHref} className={tabClass({ isActive: activeOwner === "activity" })} aria-current={activeOwner === "activity" ? "page" : undefined}>
          <ActivityIcon />
          <span>Runs</span>
        </Link>
        <Link to={tasksHref} className={tabClass({ isActive: activeOwner === "tasks" })} aria-current={activeOwner === "tasks" ? "page" : undefined}>
          <TasksIcon />
          <span>Actions</span>
        </Link>
        <Link
          to={brandHref}
          aria-current={activeOwner === "brand" ? "page" : undefined}
          className={tabClass({ isActive: activeOwner === "brand" })}
        >
          <BrandIcon />
          <span>Brand</span>
        </Link>
      </div>
    </nav>
  );
}

function WorkspaceIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v3H4V6zm0 5h16v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-7z" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14v11H9l-4 3V5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 12h5" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14M5 12h14M5 19h14" />
    </svg>
  );
}

function TasksIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function BrandIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function CampaignsIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" /></svg>;
}
